import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import FinalResultItem from "./ResultPage"; // adjust path
import { processComments } from "../Utility/StringToArray";

import { fetchTestHistory, buildQuestionAttempts, fetchQuestionResultIfNeeded } from "./Utility/dataService";
import { getCurrentUserId } from "../../../Backend/Authentication/UserStore"; // adjust path
import { processRetry } from "../TestScreen/SubmitTest";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

// Adapter: map QuestionAttempt -> FinalResultItem "resultItem" shape
function toFinalResultItem(attempt) {
  if (!attempt) return null;

  return {
    question: {
      id: attempt.question_id ?? `q_${attempt.index}`,
      text: attempt.question_text ?? `Question ${attempt.index}`,
      image_src: attempt.img_src ?? "",
    },
    verdict: attempt.verdict,
    local_url: attempt.local_url ?? null,
    user_answer_text: attempt.answer_text ?? "",

    correct_solution: attempt.correct_solution ?? "",

    comments: processComments(attempt.comments),
    pronounciation_result: [], // keep empty unless you add it later
    grammar_error: attempt.ai_analysis?.grammar_error || [],
    new_vocab: attempt.ai_analysis?.new_vocab || [],
    status: attempt.status ?? null
  };
}

export default function TestAttemptHistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const { state } = location;
  const { testId } = state || {};

  const [testMeta, setTestMeta] = useState(null); // {id,user_id,title,score,total_questions,created_at}
  const [questions, setQuestions] = useState([]); // QuestionAttempt[]
  const [selectedIndex, setSelectedIndex] = useState(0); // 0-based
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Keep selected button visible
  const stripRef = useRef(null);
  const btnRefs = useRef([]);

  // Safety: if no state (refresh), show message
  useEffect(() => {
    if (!testId) {
      setError("No test data found. Please go back and open from Test History.");
      setLoading(false);
    }
  }, [testId]);

  // Load meta + initial question attempts
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!testId) return;

      try {
        setLoading(true);
        setError("");

        // 1) meta
        const meta = await fetchTestHistory(testId);
        if (cancelled) return;
        setTestMeta(meta);

        // 2) build skeleton + merge local + merge server + hydrate question/solution
        const totalQuestions = meta?.total_questions || 0;
        if (!totalQuestions) {
          setQuestions([]);
          setLoading(false);
          return;
        }

        const built = await buildQuestionAttempts({
          testId,
          totalQuestions
        });

        if (cancelled) return;

        setQuestions(built);
        setSelectedIndex(0); // default Q1
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load test");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [testId]);

  // Keep selected tab visible
  useEffect(() => {
    const el = btnRefs.current[selectedIndex];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  // Lazy-load per question when selected changes
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!testId) return;
      if (!questions?.length) return;

      const attempt = questions[selectedIndex];
      if (!attempt) return;

      try {
        const userId = await getCurrentUserId();
        if (!userId) return;

        const row = await fetchQuestionResultIfNeeded({
          testId,
          userId,
          attempt, // should check nulls internally
        });

        if (cancelled || !row) return;

        // merge into that one question
        setQuestions((prev) => {
          const next = [...prev];
          const i = selectedIndex;
          if (!next[i]) return prev;

          next[i] = {
            ...next[i],
            question_id: row.question_id ?? next[i].question_id,
            verdict: row.verdict ?? next[i].verdict,
            answer_text: row.answer_text ?? next[i].answer_text,
            comments: row.comments ?? next[i].comments,
            ai_analysis: row.ai_analysis ?? next[i].ai_analysis,
            status: row.status ?? next[i].status
          };
          return next;
        });
      } catch (e) {
        // don't hard fail UI; keep it usable
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
    };
    // IMPORTANT: do not add `questions` here or it'll run too often
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, testId]);

  // Final score computed from questions verdicts
  const computedScore = useMemo(() => {
    const total = questions.length;
    const correct = questions.reduce((s, q) => s + (q.verdict === true ? 1 : 0), 0);
    return { correct, total };
  }, [questions]);

  // show score only when fully processed: no verdict null
  const isFullyProcessed = useMemo(() => {
    if (!questions.length) return false;
    return questions.every((q) => q.verdict !== null && q.verdict !== undefined);
  }, [questions]);

  const selectedAttempt = questions[selectedIndex] || null;
  const selectedItem = toFinalResultItem(selectedAttempt);

  const scrollStrip = (dir) => {
    const node = stripRef.current;
    if (!node) return;
    const amount = 280;
    node.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };




  const handleRetryQuestion = async () => {
    const qAttempt = questions[selectedIndex];
    if (!qAttempt) {
      alert(`Retry Failed: Unable to Find Question Details.`);
      return;
    }

    const needsRetry =
      qAttempt.status !== "success" ||
      qAttempt.verdict == null ||
      qAttempt.answer_text == null ||
      qAttempt.comments == null ||
      !qAttempt.ai_analysis; // simplified check for null/undefined

    if (!needsRetry) {
      alert(`Retry Failed`);
      return;
    }

    try {
      // 1. Optimistic UI Update: Show spinner
      setQuestions(prev => {
        const next = [...prev];
        if (next[selectedIndex]) {
          next[selectedIndex] = { ...next[selectedIndex], status: 'started' };
        }
        return next;
      });

      console.log(`🔄 Starting Retry for Q${qAttempt.index + 1}...`);

      // 2. Call the service (MUST USE AWAIT)
      await processRetry(qAttempt, testId); // <--- Pass testId and wait!

      console.log("✅ Retry initiated successfully");

    } catch (err) {
      console.error("Retry Error:", err);
      alert(`Retry Failed: ${err.message}`);

      // 3. Revert UI on failure
      setQuestions(prev => {
        const next = [...prev];
        if (next[selectedIndex]) {
          next[selectedIndex] = { ...next[selectedIndex], status: 'failed' };
        }
        return next;
      });
    }
  };

  // Render states
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto w-full max-w-5xl px-4 py-10 text-gray-600">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto w-full max-w-5xl px-4 py-10">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm
                       text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-50"
            type="button"
          >
            ← Back
          </button>
          <div className="rounded-2xl border border-gray-200 p-4 text-red-700 bg-red-50">
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Use meta for title/date if available, else fallback
  const headerTitle = testMeta?.title || `Test ${testId || ""}`;
  const headerDate = testMeta?.created_at ? formatDate(testMeta.created_at) : "";
  const totalQuestions = testMeta?.total_questions ?? questions.length;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/85 backdrop-blur border-b border-gray-100">
        <div className="mx-auto w-full max-w-5xl px-4 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="mb-2 inline-flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm
                           text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-50"
                type="button"
              >
                ← Back
              </button>

              <h1 className="truncate text-xl sm:text-2xl font-semibold text-gray-900">
                {headerTitle}
              </h1>

              <p className="mt-1 text-sm text-gray-500">
                {headerDate ? `${headerDate} • ` : ""}Test ID:{" "}
                <span className="text-gray-700">{testId}</span>
              </p>
            </div>

            {/* TOP RIGHT: show ONLY when fully processed */}
            {isFullyProcessed && (
              <div className="flex items-center gap-2 sm:pt-1">
                <div className="rounded-full px-3 py-1 text-sm ring-1 ring-inset ring-gray-200 text-gray-700 bg-gray-50">
                  Score: {computedScore.correct}/{totalQuestions}
                </div>
              </div>
            )}
          </div>

          {/* Question strip */}
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => scrollStrip("left")}
              className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-xl
                         ring-1 ring-inset ring-gray-200 hover:bg-gray-50"
              aria-label="Scroll left"
              type="button"
            >
              ‹
            </button>

            <div
              ref={stripRef}
              className="flex-1 overflow-x-auto no-scrollbar"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="flex items-center gap-2 pr-2">
                {questions.map((q, idx) => {
                  const isActive = idx === selectedIndex;

                  return (
                    <button
                      key={`${testId}-${q.index}`}
                      ref={(el) => (btnRefs.current[idx] = el)}
                      onClick={() => setSelectedIndex(idx)}
                      type="button"
                      className={[
                        "flex items-center rounded-xl px-4 py-2 text-sm font-medium whitespace-nowrap",
                        "ring-1 ring-inset transition",
                        isActive
                          ? "bg-gray-900 text-white ring-gray-200"
                          : "bg-white text-gray-800 ring-gray-200 hover:bg-gray-50 hover:ring-gray-300",
                      ].join(" ")}
                      aria-current={isActive ? "true" : "false"}
                      aria-label={`Question ${idx + 1}`}
                    >
                      Q{idx + 1}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              onClick={() => scrollStrip("right")}
              className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-xl
                         ring-1 ring-inset ring-gray-200 hover:bg-gray-50"
              aria-label="Scroll right"
              type="button"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        <FinalResultItem resultItem={selectedItem} onRetry={handleRetryQuestion} />
      </div>
    </div>
  );
}
